import { useCallback, useEffect, useState } from "react";
import { QualifiedFeature } from "fe-common/graphql";
import { SelectionType } from "../../uiControllers/AnswerButton";
import { Answer } from "../../../solverHooks/useConversation";
import { ACTION, CATEGORY, evt } from "fe-common/analytics/analytics";

type GeneralBackgroundQuestionLogic = {
  answers: QualifiedFeature[];
  answerSelectionType: SelectionType;
  onRemove: (number) => void;
  onAdd: (QualifiedFeature) => void;
  handleInitialAnswer: (SelectionType) => void;
};

/**
 * Use this hook for when you have a general yes/no background question
 * that has unique form-like flow, unlike solver question-answer like:
 * background, family history, medication, allergies, etc
 * */
export function useBackgroundQuestionLogic(
  onAnswer: (
    answer: QualifiedFeature[],
    skipped?: boolean,
    metdata?: any,
  ) => void,
  ansewr?: Answer,
  analyticsTag?: string,
  clinicName?: string,
  clinicId?: string,
): GeneralBackgroundQuestionLogic {
  const [answers, setAnswers] = useState<QualifiedFeature[]>([]);
  const [answerSelectionType, setAnswerSelectionType] = useState<SelectionType>(
    null,
  );

  const askBeforePresentingBackgroundQuestions = true; // Disabled for now, always ask

  useEffect(() => {
    if (!ansewr) {
      setAnswerSelectionType(
        askBeforePresentingBackgroundQuestions ? null : SelectionType.YES,
      );
      setAnswers([]);
    }
  }, [ansewr, askBeforePresentingBackgroundQuestions]);

  const onAdd = useCallback((qf: QualifiedFeature) => {
    setAnswers((prev) => [...prev, qf]);
  }, []);

  const onRemove = useCallback((index) => {
    setAnswers((prev) => [...prev.slice(0, index), ...prev.slice(index + 1)]);
  }, []);

  const handleInitialAnswer = useCallback(
    (selectionType: SelectionType) => {
      evt({
        category: CATEGORY.PATIENT_CHAT,
        action: ACTION.PATIENT_ANSWERED_QUESTION,
        label:
          "Q:" +
          analyticsTag +
          " A: " +
          (selectionType === SelectionType.NO ? "NO" : "YES"),
        props: {
          clinicName,
          clinicId,
        },
      });
      setAnswerSelectionType(selectionType);
      if (selectionType === SelectionType.NO) {
        onAnswer([], true);
      }
    },
    [onAnswer, analyticsTag, clinicName, clinicId],
  );
  return {
    answers,
    answerSelectionType,
    onRemove: onRemove,
    onAdd,
    handleInitialAnswer,
  };
}
